import { Box, Button, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { supportApi } from "../../client.js";
import type { AdminTicketSla } from "../../types.js";

/**
 * Staff-only per-ticket SLA override card. Two `<input
 * type="datetime-local">` fields wired to the admin endpoint:
 *
 *   PATCH /api/admin/tickets/:id/sla
 *     { sla: { response_due_at, resolution_due_at } }
 *
 * The datetime-local widget speaks `YYYY-MM-DDTHH:mm` (local TZ, no
 * seconds, no zone marker). We slice the incoming ISO-8601 to the same
 * shape on render, and on save we either pass the string verbatim
 * (Rails will parse it) or null when the input is cleared — clearing
 * the override is the operator's escape hatch back to the default
 * policy.
 *
 * Lifted to its own component so the dashboard can mount it
 * symmetrically with `<WatcherMenu>` in the ticket detail right rail.
 */
export function SlaPanel({
  ticketId,
  initial,
  onSaved,
}: {
  ticketId: number | string;
  initial: AdminTicketSla | null;
  /** Called after a successful save — `TicketDetailPage` invalidates
   * the ticket query so the new dates flow back into other widgets
   * (e.g. the list page's `sla_status` chip). */
  onSaved?: () => void;
}): JSX.Element {
  const [response, setResponse] = useState(
    isoToInput(initial?.response_due_at ?? null),
  );
  const [resolution, setResolution] = useState(
    isoToInput(initial?.resolution_due_at ?? null),
  );

  const save = useMutation({
    mutationFn: () =>
      supportApi.updateAdminTicketSla(ticketId, {
        response_due_at: response ? response : null,
        resolution_due_at: resolution ? resolution : null,
      }),
    onSuccess: () => {
      onSaved?.();
    },
  });

  return (
    <Box
      component="section"
      aria-label="SLA"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
        p: 1.5,
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
          SLA
        </Typography>
        {save.isError && (
          <Typography component="span" role="alert" sx={{ fontSize: 12, color: "error.main" }}>
            Save failed
          </Typography>
        )}
      </Box>

      <TextField
        type="datetime-local"
        label="Response due"
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        disabled={save.isPending}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        type="datetime-local"
        label="Resolution due"
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
        disabled={save.isPending}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <Button
        type="button"
        variant="contained"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        sx={{ mt: 1, width: "fit-content" }}
      >
        {save.isPending ? "Saving…" : "Save SLA"}
      </Button>
    </Box>
  );
}

/**
 * Trim an ISO-8601 string down to the `YYYY-MM-DDTHH:mm` shape
 * `<input type="datetime-local">` accepts. Anything beyond 16 chars
 * (seconds, timezone marker) confuses the widget on Safari + Firefox,
 * which silently render an empty value. `null` becomes "".
 */
function isoToInput(iso: string | null): string {
  if (!iso) return "";
  return iso.length >= 16 ? iso.slice(0, 16) : iso;
}
